import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Car, ChevronRight } from "lucide-react";
import { API } from "@/contexts/AuthContext";
import Header from "@/components/shared/Header";
import PhotoAnnotator from "@/components/shared/PhotoAnnotator";

const JobsListPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);
  const [annotatingPhoto, setAnnotatingPhoto] = useState(null);
  const navigate = useNavigate();

  const fetchJobs = () => {
    setLoading(true);
    axios.get(`${API}/jobs`)
      .then(res => setJobs(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const handleAccept = async (jobId, e, job) => {
    e.stopPropagation();
    setAccepting(jobId);
    try {
      await axios.post(`${API}/tech/inspection/${jobId}/accept`);
      
      // Navigate directly to the appropriate inspection page
      if (job?.requires_diagnostic_vi) {
        // Diagnostic VI required - go to diagnostic capture
        navigate(`/app/tech/job/${jobId}/diagnostic`);
      } else if (job?.requires_vehicle_inspection) {
        // Vehicle inspection required - go to start page to enter mileage
        navigate(`/app/tech/job/${jobId}/start`);
      } else {
        // Fallback - go to start page
        navigate(`/app/tech/job/${jobId}/start`);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to accept inspection");
      setAccepting(null);
    }
  };

  const handleAnnotationSave = (annotatedImage) => {
    // Handle saving the annotated image
    console.log("Annotated image saved:", annotatedImage);
    setAnnotatingPhoto(null);
  };

  const handleAnnotationCancel = () => {
    setAnnotatingPhoto(null);
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  // iter213d — Trust the backend. The tech queue endpoint returns
  // ONLY jobs whose ``outstanding_workflows`` is non-empty (or that
  // have no commissioned workflows at all — legacy safety net). Split
  // by ``inspection_status`` only to keep the two visual buckets
  // (awaiting acceptance vs everything else). Every job returned is
  // by definition still active — do NOT drop rows silently because of
  // a stale/legacy ``inspection_status``.
  const awaitingJobs   = jobs.filter(j => j.inspection_status === "awaiting_acceptance");
  const inProgressJobs = jobs.filter(j => j.inspection_status !== "awaiting_acceptance");

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      
      {/* Photo Annotation Modal */}
      {annotatingPhoto && (
        <PhotoAnnotator
          photo={annotatingPhoto}
          onSave={handleAnnotationSave}
          onCancel={handleAnnotationCancel}
        />
      )}
      
      <div className="p-4 max-w-2xl mx-auto">
        {/* Inspections Required - Awaiting Acceptance */}
        {awaitingJobs.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Inspections Required ({awaitingJobs.length})
            </h2>
            <div className="space-y-3">
              {awaitingJobs.map(job => (
                <Card 
                  key={job.id} 
                  className="border-amber-200 bg-amber-50"
                  data-testid={`job-card-${job.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Car className="w-5 h-5 text-amber-600" />
                          <span className="font-bold text-amber-900">{job.reg}</span>
                          <span className="text-amber-700">{job.make} {job.model}</span>
                        </div>
                        {/* Vehicle Details */}
                        {(job.year || job.colour || job.fuel_type) && (
                          <div className="mt-1 flex flex-wrap gap-2 text-xs text-amber-600">
                            {job.year && <span>{job.year}</span>}
                            {job.colour && <span>• {job.colour}</span>}
                            {job.fuel_type && <span>• {job.fuel_type}</span>}
                            {job.engine_capacity && <span>• {(job.engine_capacity / 1000).toFixed(1)}L</span>}
                          </div>
                        )}
                        {/* Key Tag Number - Prominent display for technician */}
                        {job.key_tag_number && (
                          <div className="mt-2 mb-2 px-3 py-2 bg-amber-200 border-2 border-amber-400 rounded-lg inline-block">
                            <span className="text-amber-900 font-bold text-lg">🔑 Key Tag: {job.key_tag_number}</span>
                          </div>
                        )}
                        <div className="mt-2 text-sm text-amber-700 space-y-1">
                          {/* Vehicle On Site - Car icon */}
                          <div className="flex items-center gap-1.5">
                            <Car className={`w-4 h-4 ${job.vehicle_on_site !== false ? 'text-green-600' : 'text-red-500'}`} />
                            <span className={`text-xs font-medium ${job.vehicle_on_site !== false ? 'text-green-700' : 'text-red-600'}`}>
                              {job.vehicle_on_site !== false ? 'On site' : 'Not here'}
                            </span>
                          </div>
                          {/* Inspection type pills */}
                          <div className="flex gap-1.5 mt-1">
                            {job.requires_vehicle_inspection && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700 border border-blue-200">
                                VI
                              </span>
                            )}
                            {job.requires_diagnostic_vi && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                                DIAG
                              </span>
                            )}
                          </div>
                          {job.requires_diagnostic_vi && job.customer_concern && (
                            <div className="mt-2 p-2 bg-purple-100 rounded text-purple-800">
                              <strong>Customer Concern:</strong> {job.customer_concern}
                            </div>
                          )}
                          <p><strong>Requested by:</strong> {job.requested_by_name}</p>
                        </div>
                      </div>
                      <Button 
                        onClick={(e) => handleAccept(job.id, e, job)}
                        disabled={accepting === job.id}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid={`accept-btn-${job.id}`}
                      >
                        {accepting === job.id ? "Accepting..." : "Accept"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* In Progress Inspections */}
        <h2 className="text-lg font-semibold text-slate-800 mb-3" data-testid="jobs-list-title">
          My Inspections ({inProgressJobs.length})
        </h2>
        {inProgressJobs.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-slate-500">
              <Car className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No inspections in progress</p>
              {awaitingJobs.length > 0 && (
                <p className="text-sm mt-2">Accept an inspection above to start</p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {inProgressJobs.map(job => {
              // iter213d — always route through /required-work. That
              // page consults the backend for the authoritative
              // single-source-of-truth workflow→route mapping and
              // auto-forwards when only one workflow remains, or
              // renders the chooser when 2+ remain. This keeps the
              // route mapping in exactly one place (backend
              // ``_workflow_route``) instead of duplicating it here.
              const target = `/app/tech/job/${job.id}/required-work`;
              return (
              <Card 
                key={job.id} 
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(target)}
                data-testid={`job-card-${job.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                      <div>
                      <div className="flex items-center gap-2">
                        <Car className="w-5 h-5 text-gray-500" />
                        <span className="font-semibold">{job.reg}</span>
                      </div>
                      <p className="text-sm text-gray-600">{job.make} {job.model}</p>
                      {/* Vehicle Details */}
                      {(job.year || job.colour || job.fuel_type) && (
                        <p className="text-xs text-slate-500">
                          {job.year && <span>{job.year}</span>}
                          {job.colour && <span> • {job.colour}</span>}
                          {job.fuel_type && <span> • {job.fuel_type}</span>}
                          {job.engine_capacity && <span> • {(job.engine_capacity / 1000).toFixed(1)}L</span>}
                        </p>
                      )}
                      <p className="text-xs text-slate-500 mt-1">{job.inspection_type_label}</p>

                      {/* iter213d — authoritative outstanding-workflow
                          chip so techs can see what's still owed at a
                          glance (matches the deep-link + chooser). */}
                      {Array.isArray(job.outstanding_labels) && job.outstanding_labels.length > 0 && (
                        <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-50 border border-amber-200"
                             data-testid={`outstanding-chip-${job.id}`}>
                          <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                          <span className="text-[11px] font-semibold text-amber-800">
                            Outstanding: {job.outstanding_labels.join(" · ")}
                          </span>
                        </div>
                      )}
                      <div className="flex gap-2 mt-2">
                        {job.requires_diagnostic_vi && (
                          <Badge 
                            variant={job.diagnostic_status === 'completed' ? 'default' : 'secondary'}
                            className={job.diagnostic_status === 'completed' ? 'bg-green-600' : 'bg-purple-100 text-purple-700'}
                          >
                            Diag: {job.diagnostic_status === 'completed' ? '✓ Done' : 'Pending'}
                          </Badge>
                        )}
                        {job.requires_vehicle_inspection && (
                          <Badge 
                            variant={job.vi_status === 'completed' ? 'default' : 'secondary'}
                            className={job.vi_status === 'completed' ? 'bg-green-600' : 'bg-blue-100 text-blue-700'}
                          >
                            VI: {job.vi_status === 'completed' ? '✓ Done' : 'Pending'}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Show customer concern preview for diagnostic jobs */}
                      {job.requires_diagnostic_vi && job.customer_concern && job.diagnostic_status !== 'completed' && (
                        <div className="mt-2 p-2 bg-purple-50 rounded text-xs text-purple-700 max-w-md">
                          <strong>Concern:</strong> {job.customer_concern.length > 80 ? job.customer_concern.substring(0, 80) + '...' : job.customer_concern}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge variant="secondary">In Progress</Badge>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}

        {/* Refresh Button */}
        <div className="mt-6 text-center">
          <Button variant="outline" size="sm" onClick={fetchJobs}>
            Refresh List
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JobsListPage;
